import { matchPath, useHistory, useLocation } from 'react-router-dom';
import {
  ROUTE_FLOAT_PLAN_LIST,
  ROUTE_MAP,
  ROUTE_NOTIFICATIONS,
  ROUTE_SETTINGS,
  ROUTE_TRIP_ACTIVE,
} from '../../../services/route/route-names';
import { transitionToNextRoute } from '../../../services/route/route-navigation';

// eslint-disable-next-line no-shadow
export enum MenuItemEnum {
  PLAN = 'plan',
  PREPARE = 'prepare',
  // Value kept as 'go' so the menu-bar analytics event name stays continuous
  TRACK = 'go',
  WEATHER = 'weather',
  ALERTS = 'alerts',
  GROUPS = 'groups',
  PLANS = 'plans',
  SETTINGS = 'settings',
  YOU = 'you',
}

const PREPARE_PATHS = [
  '/maps/prepare',
  '/maps/marketplace',
  '/maps/courses',
  '/maps/insurance',
  '/maps/partner',
  '/maps/reminders',
];
const WEATHER_PATHS = ['/maps/weather'];
const ALERTS_PATHS = ['/maps/notifications'];
const GROUPS_PATHS = ['/maps/signal'];
// The float plan list, creating/updating a plan and viewing one
const PLANS_PATHS = [
  '/maps/float-plans',
  '/maps/float-plan',
  '/maps/plan',
  '/maps/trips/plan',
];
const SETTINGS_PATHS = ['/maps/settings'];
const YOU_PATHS = [
  '/maps/you',
  '/maps/contact-support',
  '/maps/login',
  '/maps/member',
  '/maps/menu',
  '/maps/notifications',
  '/maps/notifications/alerts',
  '/maps/settings',
  '/maps/share-feedback',
  '/maps/trips',
  '/maps/float-plans',
  '/authentication/email-change-reauth',
  '/authentication/email-change-confirm',
];

// Shared pickers that hand back to whichever page opened them via `returnUrl`
const SHARED_PICKER_PATHS = ['/maps/vessels'];

interface ActiveMenuOptions {
  /**
   * Treat notification, signal, float plan and settings routes as their own
   * Alerts, Groups, Plans and Settings sections. The desktop sidebar has these
   * items; the mobile menu bar does not, so it keeps them under You.
   */
  desktopSections?: boolean;
}

const matchesAny = (pathname: string, paths: string[]) =>
  Boolean(matchPath(pathname, { path: paths, exact: false }));

/** Works out which main app section a pathname belongs to. */
export const getActiveMenu = (
  pathname: string,
  { desktopSections = false }: ActiveMenuOptions = {},
): MenuItemEnum => {
  if (matchesAny(pathname, PREPARE_PATHS)) return MenuItemEnum.PREPARE;
  if (matchesAny(pathname, WEATHER_PATHS)) return MenuItemEnum.WEATHER;
  if (desktopSections) {
    if (matchesAny(pathname, ALERTS_PATHS)) return MenuItemEnum.ALERTS;
    if (matchesAny(pathname, GROUPS_PATHS)) return MenuItemEnum.GROUPS;
    if (matchesAny(pathname, PLANS_PATHS)) return MenuItemEnum.PLANS;
    if (matchesAny(pathname, SETTINGS_PATHS)) return MenuItemEnum.SETTINGS;
  }
  if (matchesAny(pathname, YOU_PATHS)) return MenuItemEnum.YOU;
  return MenuItemEnum.PLAN;
};

/**
 * Works out which main app section is active from the current route.
 * Shared by the mobile menu bar and the desktop sidebar. Shared pickers (e.g.
 * choosing a vessel) keep the section of the page that opened them.
 */
export const useActiveMenu = (
  options: ActiveMenuOptions = {},
): MenuItemEnum => {
  const { pathname, state } = useLocation<{ returnUrl?: string } | undefined>();
  const returnUrl = state?.returnUrl;
  const effectivePath =
    returnUrl && matchesAny(pathname, SHARED_PICKER_PATHS)
      ? returnUrl
      : pathname;

  return getActiveMenu(effectivePath, options);
};

/** Returns a function that navigates to the given main app section. */
export const useNavigateToMenu = () => {
  const history = useHistory();

  return (menuName: MenuItemEnum) => {
    switch (menuName) {
      case MenuItemEnum.YOU:
        history.push('/maps/you');
        break;
      case MenuItemEnum.PREPARE:
        history.push('/maps/prepare');
        break;
      case MenuItemEnum.GROUPS:
        // No route name exists for the community group; it's a nested route
        // inside SignalFeature
        history.push('/maps/signal/groups/community');
        break;
      case MenuItemEnum.TRACK:
        transitionToNextRoute(ROUTE_TRIP_ACTIVE);
        break;
      case MenuItemEnum.WEATHER:
        // The bare weather URL's route name is generated (`…HOME`), so use the path
        history.push('/maps/weather');
        break;
      case MenuItemEnum.ALERTS:
        transitionToNextRoute(ROUTE_NOTIFICATIONS);
        break;
      case MenuItemEnum.PLANS:
        transitionToNextRoute(ROUTE_FLOAT_PLAN_LIST);
        break;
      case MenuItemEnum.SETTINGS:
        transitionToNextRoute(ROUTE_SETTINGS);
        break;
      case MenuItemEnum.PLAN:
        transitionToNextRoute(ROUTE_MAP);
        break;
      default:
    }
  };
};
